/**
 * 회원가입 폼: Form.ts로 AJAX 제출.
 * 성공 시 인증번호 입력 단계 표시 → 코드 제출 후 가입 완료.
 * Form must have id="auth-signup-form", data-submit-url. 인증 단계: #auth-signup-verify, #auth-signup-verify-form.
 */
import { Form } from "../../core/Form";
import { Modal } from "../../utils/Modal";

const FORM_ID = "auth-signup-form";
const ERROR_BOX_ID = "auth-signup-error";
const VERIFY_SECTION_ID = "auth-signup-verify";
const VERIFY_FORM_ID = "auth-signup-verify-form";
const VERIFY_EMAIL_ID = "auth-signup-verify-email";
const VERIFY_EMAIL_INPUT_ID = "auth-signup-verify-email-input";
const VERIFY_ERROR_ID = "auth-signup-verify-error";
const VERIFY_BTN_ID = "auth-signup-verify-btn";

function getErrorBox(): HTMLElement | null {
  return document.getElementById(ERROR_BOX_ID);
}

function renderError(message: string | null): void {
  const box = getErrorBox();
  if (!box) return;
  if (message === null || message === "") {
    box.innerHTML = "";
    box.setAttribute("hidden", "");
    return;
  }
  const wrap = document.createElement("div");
  wrap.className = "auth-page__error";
  wrap.textContent = message;
  box.innerHTML = "";
  box.appendChild(wrap);
  box.removeAttribute("hidden");
}

function getVerifyErrorBox(): HTMLElement | null {
  return document.getElementById(VERIFY_ERROR_ID);
}

function renderVerifyError(message: string | null): void {
  const box = getVerifyErrorBox();
  if (!box) return;
  if (message === null || message === "") {
    box.textContent = "";
    box.setAttribute("hidden", "");
    return;
  }
  box.textContent = message;
  box.removeAttribute("hidden");
}

function getFirstError(errors: Record<string, string[]> | undefined): string {
  if (!errors || typeof errors !== "object") return "";
  const first = Object.values(errors).flat()[0];
  return typeof first === "string" ? first : "";
}

function isKo(): boolean {
  return document.documentElement.lang?.startsWith("ko") ?? false;
}

function showVerifyStep(email: string): void {
  const form = document.getElementById(FORM_ID);
  const verifySection = document.getElementById(VERIFY_SECTION_ID);
  const emailDisplay = document.getElementById(VERIFY_EMAIL_ID);
  const emailInput = document.getElementById(VERIFY_EMAIL_INPUT_ID) as HTMLInputElement | null;
  const codeInput = document.getElementById("auth-signup-code") as HTMLInputElement | null;
  if (form) form.setAttribute("hidden", "");
  if (verifySection) {
    verifySection.removeAttribute("hidden");
    if (emailDisplay) emailDisplay.textContent = email;
    if (emailInput) emailInput.value = email;
    if (codeInput) {
      codeInput.value = "";
      codeInput.focus();
    }
  }
}

export function initAuthSignupForm(): void {
  const form = document.getElementById(FORM_ID) as HTMLFormElement | null;
  if (!form) return;

  const submitUrl = form.dataset.submitUrl ?? form.action;
  if (!submitUrl) return;

  new Form(form, {
    submitUrl,
    submitButtonSelector: "button[type=submit]",
    loaderSelector: "",
    requestFailedMessage: isKo() ? "요청을 보낼 수 없습니다." : "Request failed.",
    invalidResponseMessage: isKo() ? "응답을 처리할 수 없습니다." : "Invalid response.",
    onSuccess: async (data) => {
      const payload = data?.data as { redirect?: string; email?: string; need_verify?: boolean } | undefined;
      const needVerify = payload?.need_verify === true;
      const email = payload?.email as string | undefined;
      const msg = (data?.message ?? data?.msg ?? "") as string;

      if (needVerify && email) {
        showVerifyStep(email);
        return;
      }
      const redirect = payload?.redirect;
      const title = isKo() ? "회원가입" : "Sign Up";
      if (msg) await Modal.alert(msg, title);
      if (redirect) window.location.href = redirect;
    },
    onFail: (message, data) => {
      const errors = data?.errors as Record<string, string[]> | undefined;
      const first = getFirstError(errors);
      renderError(first || message || (isKo() ? "회원가입에 실패했습니다." : "Sign up failed."));
    },
  }).init();

  form.addEventListener("submit", () => renderError(null));

  // 인증번호 폼
  const verifyForm = document.getElementById(VERIFY_FORM_ID) as HTMLFormElement | null;
  if (!verifyForm) return;
  const verifyUrl = verifyForm.dataset.submitUrl ?? verifyForm.action;
  if (!verifyUrl) return;

  verifyForm.addEventListener("submit", () => renderVerifyError(null));

  new Form(verifyForm, {
    submitUrl: verifyUrl,
    submitButtonSelector: "#" + VERIFY_BTN_ID,
    loaderSelector: "#" + VERIFY_BTN_ID + " .auth-page__btn-loader",
    requestFailedMessage: isKo() ? "요청을 보낼 수 없습니다." : "Request failed.",
    invalidResponseMessage: isKo() ? "응답을 처리할 수 없습니다." : "Invalid response.",
    onSuccess: async (data) => {
      const msg = (data?.message ?? data?.msg ?? "") as string;
      const redirect = (data?.data as { redirect?: string } | undefined)?.redirect;
      const title = isKo() ? "회원가입" : "Sign Up";
      if (msg) await Modal.alert(msg, title);
      if (redirect) window.location.href = redirect;
    },
    onFail: (message) => {
      renderVerifyError(message || (isKo() ? "인증번호를 확인해 주세요." : "Please check the code."));
    },
  }).init();
}
